/** Seasons and Jewish holidays for the bridge scene, read from the device's own calendar. */

export type Season = 'autumn' | 'winter' | 'spring' | 'summer';
export type Holiday =
  | { id: 'rosh-hashana' }
  | { id: 'sukkot' }
  | { id: 'hanukkah'; night: number }
  | { id: 'tu-bishvat' }
  | { id: 'purim' }
  | { id: 'pesach' }
  | { id: 'shavuot' };

// Israel: the first rain in autumn, blossom in spring, long dry summer.
export function seasonOf(d: Date): Season {
  const m = d.getMonth();
  if (m >= 8 && m <= 10) return 'autumn';
  if (m === 11 || m <= 1) return 'winter';
  if (m <= 4) return 'spring';
  return 'summer';
}

let hebrewFmt: Intl.DateTimeFormat | null | undefined;
function hebrewDate(d: Date): { day: number; month: string } | null {
  if (hebrewFmt === undefined) {
    try {
      hebrewFmt = new Intl.DateTimeFormat('en-u-ca-hebrew', { day: 'numeric', month: 'long' });
    } catch {
      hebrewFmt = null;
    }
  }
  if (!hebrewFmt) return null;
  const parts = hebrewFmt.formatToParts(d);
  const day = Number(parts.find((p) => p.type === 'day')?.value);
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  return Number.isFinite(day) ? { day, month } : null;
}

export function holidayOf(now: Date): Holiday | null {
  // A Jewish day begins at nightfall: from 17:00 the scene already shows tomorrow's holiday.
  const d = new Date(now.getTime() + 7 * 3600e3);
  const h = hebrewDate(d);
  if (!h) return null;
  const { day, month } = h;
  if (month === 'Tishri' && day <= 2) return { id: 'rosh-hashana' };
  if (month === 'Tishri' && day >= 15 && day <= 22) return { id: 'sukkot' };
  if (month === 'Shevat' && day === 15) return { id: 'tu-bishvat' };
  if ((month === 'Adar' || month === 'Adar II') && day === 14) return { id: 'purim' };
  if (month === 'Nisan' && day >= 15 && day <= 21) return { id: 'pesach' };
  if (month === 'Sivan' && day === 6) return { id: 'shavuot' };
  // Hanukkah starts on 25 Kislev and crosses into Tevet; count back to find the night.
  for (let back = 0; back < 8; back++) {
    const p = hebrewDate(new Date(d.getTime() - back * 864e5));
    if (p?.month === 'Kislev' && p.day === 25) return { id: 'hanukkah', night: back + 1 };
  }
  return null;
}

export const HOLIDAY_LINES: Record<Holiday['id'], string> = {
  'rosh-hashana': 'שנה טובה ומתוקה!',
  sukkot: 'חג סוכות שמח!',
  hanukkah: 'חנוכה שמח!',
  'tu-bishvat': 'חג לעצים!',
  purim: 'פורים שמח!',
  pesach: 'חג אביב שמח!',
  shavuot: 'חג שבועות שמח!',
};
